import { createHabitsLevel } from "./create-habits-level";

export type SchoolbagDropZoneId = "schoolbag" | "home-basket";

export interface SchoolbagItem {
  id: string;
  label: string;
  audio: string;
}

export interface SchoolbagDropZone {
  id: SchoolbagDropZoneId;
  label: string;
  audio: string;
}

export interface SchoolbagPlacement {
  itemId: string;
  dropZoneId: SchoolbagDropZoneId;
}

export interface PackSchoolbagPayload {
  items: SchoolbagItem[];
  dropZones: SchoolbagDropZone[];
  correctPlacements: SchoolbagPlacement[];
}

function createDropZones(): SchoolbagDropZone[] {
  return [
    {
      id: "schoolbag",
      label: "放进书包",
      audio: "/audio/habits/schoolbag-zone-schoolbag.ogg"
    },
    {
      id: "home-basket",
      label: "放回收纳篮",
      audio: "/audio/habits/schoolbag-zone-home-basket.ogg"
    }
  ];
}

type SchoolbagItemInput = Omit<SchoolbagItem, "audio">;

function withSchoolbagAudio(
  payload: Omit<PackSchoolbagPayload, "items"> & {
    items: SchoolbagItemInput[];
  }
): PackSchoolbagPayload {
  return {
    ...payload,
    items: payload.items.map((item) => ({
      ...item,
      audio: "/audio/habits/schoolbag-item-" + item.id + ".ogg"
    }))
  };
}

export const packSchoolbagLevels = [
  createHabitsLevel<PackSchoolbagPayload>({
    activityType: "pack-schoolbag",
    difficulty: 1,
    objectives: ["认识每天上学常用的物品", "练习按用途整理物品"],
    title: "每天要带什么",
    instruction: "先点一件物品，再点书包或收纳篮。上学要用的装进书包，今天不用的放回篮子。",
    payload: withSchoolbagAudio({
      items: [
        { id: "pencil-case", label: "铅笔盒" },
        { id: "practice-book", label: "练习本" },
        { id: "water-bottle", label: "水壶" },
        { id: "toy-car", label: "玩具小车" }
      ],
      dropZones: createDropZones(),
      correctPlacements: [
        { itemId: "pencil-case", dropZoneId: "schoolbag" },
        { itemId: "practice-book", dropZoneId: "schoolbag" },
        { itemId: "water-bottle", dropZoneId: "schoolbag" },
        { itemId: "toy-car", dropZoneId: "home-basket" }
      ]
    })
  }),
  createHabitsLevel<PackSchoolbagPayload>({
    activityType: "pack-schoolbag",
    difficulty: 2,
    objectives: ["根据美术活动准备用品", "区分学习用品和玩具"],
    title: "美术课的书包",
    instruction: "今天要画画啦。找出美术课和每天上学要用的东西，把它们装进书包。",
    payload: withSchoolbagAudio({
      items: [
        { id: "crayons", label: "蜡笔" },
        { id: "drawing-pad", label: "画纸本" },
        { id: "art-smock", label: "防污围裙" },
        { id: "pencil-case", label: "铅笔盒" },
        { id: "building-blocks", label: "积木盒" }
      ],
      dropZones: createDropZones(),
      correctPlacements: [
        { itemId: "crayons", dropZoneId: "schoolbag" },
        { itemId: "drawing-pad", dropZoneId: "schoolbag" },
        { itemId: "art-smock", dropZoneId: "schoolbag" },
        { itemId: "pencil-case", dropZoneId: "schoolbag" },
        { itemId: "building-blocks", dropZoneId: "home-basket" }
      ]
    })
  }),
  createHabitsLevel<PackSchoolbagPayload>({
    activityType: "pack-schoolbag",
    difficulty: 3,
    objectives: ["根据体育活动准备用品", "记得带好饮水用品"],
    title: "体育课准备好",
    instruction: "体育课要动一动。把运动用品、水壶和常用文具找出来，装进书包。",
    payload: withSchoolbagAudio({
      items: [
        { id: "sports-shoes", label: "运动鞋" },
        { id: "small-towel", label: "小毛巾" },
        { id: "water-bottle", label: "水壶" },
        { id: "pencil-case", label: "铅笔盒" },
        { id: "practice-book", label: "练习本" },
        { id: "remote-control", label: "遥控器" }
      ],
      dropZones: createDropZones(),
      correctPlacements: [
        { itemId: "sports-shoes", dropZoneId: "schoolbag" },
        { itemId: "small-towel", dropZoneId: "schoolbag" },
        { itemId: "water-bottle", dropZoneId: "schoolbag" },
        { itemId: "pencil-case", dropZoneId: "schoolbag" },
        { itemId: "practice-book", dropZoneId: "schoolbag" },
        { itemId: "remote-control", dropZoneId: "home-basket" }
      ]
    })
  }),
  createHabitsLevel<PackSchoolbagPayload>({
    activityType: "pack-schoolbag",
    difficulty: 4,
    objectives: ["为雨天上学做好准备", "练习照顾自己的备用物品"],
    title: "雨天也从容",
    instruction: "窗外可能会下雨。和大人一起挑出雨天用品和学习用品，把它们放进书包。",
    payload: withSchoolbagAudio({
      items: [
        { id: "folding-umbrella", label: "折叠伞" },
        { id: "spare-socks", label: "备用袜子" },
        { id: "pencil-case", label: "铅笔盒" },
        { id: "practice-book", label: "练习本" },
        { id: "water-bottle", label: "水壶" },
        { id: "sand-toy", label: "沙滩玩具" }
      ],
      dropZones: createDropZones(),
      correctPlacements: [
        { itemId: "folding-umbrella", dropZoneId: "schoolbag" },
        { itemId: "spare-socks", dropZoneId: "schoolbag" },
        { itemId: "pencil-case", dropZoneId: "schoolbag" },
        { itemId: "practice-book", dropZoneId: "schoolbag" },
        { itemId: "water-bottle", dropZoneId: "schoolbag" },
        { itemId: "sand-toy", dropZoneId: "home-basket" }
      ]
    })
  }),
  createHabitsLevel<PackSchoolbagPayload>({
    activityType: "pack-schoolbag",
    difficulty: 5,
    objectives: ["记得归还借阅物品", "按当天任务检查书包"],
    title: "记得归还图书",
    instruction: "借来的图书明天要回家了。把要归还的东西和日常用品一起装进书包。",
    payload: withSchoolbagAudio({
      items: [
        { id: "library-book", label: "借阅的图书" },
        { id: "bookmark", label: "书签" },
        { id: "pencil-case", label: "铅笔盒" },
        { id: "practice-book", label: "练习本" },
        { id: "water-bottle", label: "水壶" },
        { id: "puzzle-box", label: "拼图盒" },
        { id: "toy-drum", label: "玩具鼓" }
      ],
      dropZones: createDropZones(),
      correctPlacements: [
        { itemId: "library-book", dropZoneId: "schoolbag" },
        { itemId: "bookmark", dropZoneId: "schoolbag" },
        { itemId: "pencil-case", dropZoneId: "schoolbag" },
        { itemId: "practice-book", dropZoneId: "schoolbag" },
        { itemId: "water-bottle", dropZoneId: "schoolbag" },
        { itemId: "puzzle-box", dropZoneId: "home-basket" },
        { itemId: "toy-drum", dropZoneId: "home-basket" }
      ]
    })
  }),
  createHabitsLevel<PackSchoolbagPayload>({
    activityType: "pack-schoolbag",
    difficulty: 6,
    objectives: ["根据第二天的安排独立准备", "完成出门前的书包检查"],
    title: "照着安排来检查",
    instruction: "明天有语文、数学和美术活动。照着安排找齐用品，再检查铅笔盒和水壶。",
    payload: withSchoolbagAudio({
      items: [
        { id: "chinese-book", label: "语文书" },
        { id: "math-practice-book", label: "数学练习本" },
        { id: "crayons", label: "蜡笔" },
        { id: "drawing-pad", label: "画纸本" },
        { id: "pencil-case", label: "铅笔盒" },
        { id: "water-bottle", label: "水壶" },
        { id: "jump-rope", label: "跳绳" },
        { id: "plush-toy", label: "毛绒玩具" }
      ],
      dropZones: createDropZones(),
      correctPlacements: [
        { itemId: "chinese-book", dropZoneId: "schoolbag" },
        { itemId: "math-practice-book", dropZoneId: "schoolbag" },
        { itemId: "crayons", dropZoneId: "schoolbag" },
        { itemId: "drawing-pad", dropZoneId: "schoolbag" },
        { itemId: "pencil-case", dropZoneId: "schoolbag" },
        { itemId: "water-bottle", dropZoneId: "schoolbag" },
        { itemId: "jump-rope", dropZoneId: "home-basket" },
        { itemId: "plush-toy", dropZoneId: "home-basket" }
      ]
    })
  })
];
